import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Building2, Users, Calendar, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LeadCaptureModal from '@/components/LeadCaptureModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [featuredUnits, setFeaturedUnits] = useState([]);
  const [scrollY, setScrollY] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalInterest, setModalInterest] = useState('');
  const videoRef = useState(null)[0];
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      name: "Shailaja Yadav",
      role: "Tenant",
      property: "",
      image: "https://static.prod-images.emergentagent.com/jobs/86556e1a-7960-491a-9ec5-7235bfecd250/images/722c998a166d351e85ae376454573533d5345f332e6f83de6afff2b1eef8b15d.png",
      review: "Best Real Estate and Property Management company. I will surely recommend it to all my friends and relatives."
    },
    {
      name: "Harsh Jariwala",
      role: "Tenant",
      property: "",
      image: "https://static.prod-images.emergentagent.com/jobs/86556e1a-7960-491a-9ec5-7235bfecd250/images/722c998a166d351e85ae376454573533d5345f332e6f83de6afff2b1eef8b15d.png",
      review: "One of the best and beautiful lofts I am staying into. The Developers GN Management has completely changed the game of the renters market. The details in the apartment I am staying into is so charismatic. Pot fillers, inbuilt ceiling speakers, Italian Kitchen, my appliances (Italian make) are bang-up what any renter could would have only dreamt of. Talking about the management! Super duper friendly, very proactive on any queries. The details in the bathroom and kitchen are worth to checkout."
    },
    {
      name: "Maricela Chocho",
      role: "Tenant",
      property: "",
      image: "https://static.prod-images.emergentagent.com/jobs/86556e1a-7960-491a-9ec5-7235bfecd250/images/722c998a166d351e85ae376454573533d5345f332e6f83de6afff2b1eef8b15d.png",
      review: "A very cozy place for you and your family, they have very professional and friendly people 100% recommended."
    },
    {
      name: "Jesus Lara",
      role: "Tenant",
      property: "",
      image: "https://static.prod-images.emergentagent.com/jobs/86556e1a-7960-491a-9ec5-7235bfecd250/images/722c998a166d351e85ae376454573533d5345f332e6f83de6afff2b1eef8b15d.png",
      review: "We have recently moved around two weeks ago and we love our new place! GN Management has made our experience stress free. They are very responsive in such a short time and care for all their tenants. Great experience so far."
    },
    {
      name: "Evans McDonald",
      role: "Tenant",
      property: "",
      image: "https://static.prod-images.emergentagent.com/jobs/86556e1a-7960-491a-9ec5-7235bfecd250/images/722c998a166d351e85ae376454573533d5345f332e6f83de6afff2b1eef8b15d.png",
      review: "My experience with GN team has been phenomenal from sales customer service, maintenance response time. The maintenance team are great guys and goes an extra mile to sort everything. I highly recommend their service."
    }
  ];

  // Optimized scroll handler with debouncing
  useEffect(() => {
    let rafId = null;
    let lastScrollY = 0;
    
    const handleScroll = () => {
      if (rafId) return; // Skip if animation frame is already pending
      
      rafId = requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        // Only update if scroll changed by more than 10px to reduce re-renders
        if (Math.abs(currentScrollY - lastScrollY) > 10) {
          setScrollY(currentScrollY);
          lastScrollY = currentScrollY;
        }
        rafId = null;
      });
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    fetchFeaturedProjects();
    fetchFeaturedUnits();
  }, []);

  // Auto-scroll testimonials every 5 seconds (reduced frequency for better performance)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => 
        prev === testimonials.length - 1 ? 0 : prev + 1
      );
    }, 5000); // Increased from 3s to 5s

    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Video autoplay handling
  useEffect(() => {
    const video = document.querySelector('#hero-video');
    if (video) {
      const playVideo = async () => {
        try {
          video.muted = true;
          video.playbackRate = 1.0;
          await video.play();
        } catch (error) {
          console.log('Video autoplay not supported, showing fallback image');
        }
      };
      // Start playing after a short delay to ensure the video is loaded
      setTimeout(playVideo, 500);
    }
  }, []);

  const fetchFeaturedProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      // Filter for home_featured projects, sort by display order, and limit to 3
      const homeFeatured = response.data
        .filter(p => p.home_featured === true)
        .sort((a, b) => {
          // Items with display_order come first, sorted by their order
          // Items without display_order come last
          if (a.home_display_order && b.home_display_order) {
            return a.home_display_order - b.home_display_order;
          }
          if (a.home_display_order) return -1;
          if (b.home_display_order) return 1;
          return 0;
        })
        .slice(0, 3);
      setFeaturedProjects(homeFeatured);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchFeaturedUnits = async () => {
    try {
      const response = await axios.get(`${API}/units`);
      // Filter for home_featured units, sort by display order, and limit to 3
      const homeFeatured = response.data
        .filter(u => u.home_featured === true)
        .sort((a, b) => {
          if (a.home_display_order && b.home_display_order) {
            return a.home_display_order - b.home_display_order;
          }
          if (a.home_display_order) return -1;
          if (b.home_display_order) return 1;
          return 0;
        })
        .slice(0, 3);
      setFeaturedUnits(homeFeatured);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const stats = [
    { label: 'Units Managed', value: '267+', icon: Building2 },
    { label: 'Square Feet', value: '1M+', icon: TrendingUp },
    { label: 'Years in Market', value: '10+', icon: Calendar },
    { label: 'Completed Projects', value: '50+', icon: Users },
  ];

  return (
    <div className="home-page">
      {/* Hero Section with Video Background */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Video/Image Background */}
        <div className="absolute inset-0 z-0">
          {/* Fallback Background Image - shows while video loads */}
          <div 
            className="absolute w-full h-full bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80)',
              filter: 'brightness(0.7) contrast(1.15) saturate(1.05)'
            }}
          />
          {/* Hero Video - New Jersey Drone Footage (Cloudinary auto-format for max browser compatibility) */}
          <video
            id="hero-video"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster="https://res.cloudinary.com/dzk6j0kr2/video/upload/so_0,q_auto,w_1920,f_jpg/gn-management/brand/hero_video.jpg"
            className="absolute w-full h-full object-cover"
            style={{ 
              filter: 'brightness(0.75) contrast(1.1) saturate(1.05)',
              objectFit: 'cover'
            }}
          >
            <source
              src="https://res.cloudinary.com/dzk6j0kr2/video/upload/f_webm,vc_vp9,q_auto/gn-management/brand/hero_video.webm"
              type="video/webm"
            />
            <source
              src="https://res.cloudinary.com/dzk6j0kr2/video/upload/f_mp4,vc_h264,q_auto/gn-management/brand/hero_video.mp4"
              type="video/mp4"
            />
            <source
              src="https://res.cloudinary.com/dzk6j0kr2/video/upload/v1784037649/gn-management/brand/hero_video.mp4"
              type="video/mp4"
            />
          </video>
          {/* Overlay with teal tint to blend with website theme and ensure text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-[#0B1220]/45 to-black/60" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#20B2AA]/5 via-transparent to-[#20B2AA]/5" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center text-white">
          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 animate-in"
            style={{ animationDelay: '0.1s' }}
          >
            Crafting Urban Living Spaces that Inspire and Endure
          </h1>
          <p
            className="text-lg sm:text-xl lg:text-2xl mb-10 max-w-3xl mx-auto font-light animate-in text-[#20B2AA]"
            style={{ animationDelay: '0.3s', textShadow: '0 2px 15px rgba(0,0,0,0.7)' }}
          >
            From skyline-defining towers to community-centric residences, GN Management builds spaces where design meets purpose.
          </p>
          <div
            className="flex flex-col sm:flex-row gap-4 justify-center animate-in"
            style={{ animationDelay: '0.5s' }}
          >
            <Button
              data-testid="speak-advisor-btn"
              size="lg"
              onClick={() => {
                setModalTitle('Connect with Our Team');
                setModalInterest('Investor');
                setIsModalOpen(true);
              }}
              className="bg-[#20B2AA] hover:bg-[#189a93] text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-200"
            >
              Connect with Our Team
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
            <Link to="/portfolio">
              <Button
                data-testid="view-portfolio-btn"
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-black px-8 py-6 text-lg rounded-full transition-all duration-200"
              >
                Our Portfolio
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="py-20 bg-gradient-to-r from-slate-100 via-gray-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  data-testid={`stat-${index}`}
                  className="text-center scale-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <Icon className="h-10 w-10 mx-auto mb-4 text-[#20B2AA]" />
                  <div className="text-4xl font-bold mb-2 text-black">{stat.value}</div>
                  <div className="text-sm text-gray-600 uppercase tracking-wide">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Buildings Section */}
      <section className="relative py-24 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
        {/* Simplified static background elements (parallax removed for performance) */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-bl from-[#20B2AA]/5 to-transparent" />
        <div className="absolute bottom-0 left-0 w-1/2 h-2/3 bg-gradient-to-tr from-teal-100/30 to-transparent" />
        
        {/* Simplified decorative shapes (reduced animation for performance) */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-1/4 w-32 h-32 border-4 border-[#20B2AA]/20 rounded-lg rotate-45" />
          <div className="absolute bottom-40 left-1/3 w-24 h-24 border-4 border-teal-300/20 rounded-full" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-black">Explore Our Signature Spaces</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Step inside GN's collection of architectural icons - where innovation meets elevation across every skyline we touch.
            </p>
          </div>
          {featuredProjects.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {featuredProjects.map((project, index) => (
                  <Link
                    to={`/portfolio/${project.slug || project.id}`}
                    key={project.id}
                    data-testid={`featured-project-${index}`}
                  >
                    <div
                      className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl bg-white scale-in cursor-pointer"
                      style={{ animationDelay: `${index * 0.08}s` }}
                    >
                      <div className="h-96 overflow-hidden">
                        <img
                          src={project.image_url || 'https://images.unsplash.com/photo-1645510807290-cc82de2749f2?crop=entropy&cs=srgb&fm=jpg&q=85'}
                          alt={project.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                      </div>
                      {/* Permanent dark gradient at bottom for text visibility */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent pointer-events-none" />
                      {/* Stronger hover gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                        <h3 className="text-2xl font-bold mb-2 drop-shadow-lg whitespace-pre-line">{project.name}</h3>
                        <p className="text-sm text-gray-200 drop-shadow-md">{project.address}</p>
                        {project.units && (
                          <p className="text-sm mt-2 text-gray-200 drop-shadow-md">{project.units} Units</p>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="text-center mt-12">
                <Link to="/portfolio">
                  <Button
                    data-testid="view-all-buildings-btn"
                    size="lg"
                    className="bg-black hover:bg-gray-800 text-white px-8 py-6 text-lg rounded-full hover:-translate-y-1 transition-all duration-200"
                  >
                    View All Properties
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-600">
              <p>No featured buildings selected. Use the admin panel to feature buildings on the homepage.</p>
            </div>
          )}
        </div>
      </section>

      {/* Featured Units Section */}
      <section className="relative py-24 bg-gradient-to-br from-cyan-50 via-teal-50 to-blue-50 overflow-hidden">
        {/* Simplified static background elements (animations removed for performance) */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 right-20 w-96 h-96 bg-[#20B2AA]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-20 w-80 h-80 bg-teal-300/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-200/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-black">
              Your Next Address Awaits
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover handpicked residences curated for modern lifestyles - now available for rent or sale across our vibrant communities
            </p>
          </div>
          {featuredUnits.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {featuredUnits.map((unit, index) => (
                  <Link
                    to={`/units/${unit.slug || unit.id}`}
                    key={unit.id}
                    data-testid={`featured-unit-${index}`}
                  >
                    <div
                      className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl hover:shadow-[#20B2AA]/40 bg-white scale-in cursor-pointer border border-gray-100 hover:border-[#20B2AA] transition-all duration-300"
                      style={{ animationDelay: `${index * 0.08}s` }}
                    >
                      <div className="h-96 overflow-hidden">
                        <img
                          src={unit.images[0] || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800'}
                          alt={unit.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute top-4 right-4 bg-gradient-to-r from-[#20B2AA] to-teal-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg z-10">
                          FEATURED
                        </div>
                      </div>
                      {/* Permanent dark gradient at bottom for text visibility */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent pointer-events-none" />
                      {/* Stronger hover gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                        <h3 className="text-2xl font-bold mb-2 drop-shadow-lg">{unit.name}</h3>
                        <p className="text-sm text-gray-200 drop-shadow-md">{unit.location}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-sm text-gray-200 drop-shadow-md">{unit.property_type}</span>
                          <span className="text-sm text-gray-200">•</span>
                          <span className="text-sm text-gray-200 drop-shadow-md">${unit.price.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="text-center mt-12">
                <Link to="/units">
                  <Button
                    data-testid="view-all-units-btn"
                    size="lg"
                    className="bg-[#20B2AA] hover:bg-[#189a93] text-white px-8 py-6 text-lg rounded-full hover:-translate-y-1 transition-all duration-200"
                  >
                    Explore Available Listings
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-600">
              <p>No featured units selected. Use the admin panel to feature units on the homepage.</p>
            </div>
          )}
        </div>
      </section>

      {/* What We Do Section */}
      <section className="relative py-24 bg-gradient-to-br from-gray-900 to-black text-white overflow-hidden">
        {/* Static background elements (animation removed for performance) */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#20B2AA] rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#20B2AA] rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-16 slide-in-up">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent">
              What We Do
            </h2>
            <p className="text-xl text-[#20B2AA] font-light">
              Shaping Neighborhoods with Purpose and Vision
            </p>
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
            {/* Left Column - Content */}
            <div className="space-y-6 slide-in-left">
              <div className="relative">
                <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#20B2AA] to-transparent" />
                <p className="text-lg text-gray-300 leading-relaxed">
                  At GN Management, we don't just build properties - we shape communities and reimagine urban living across the United States. What began over a decade ago in New Jersey has grown into a transformative real estate force, with a portfolio ranging from boutique apartments to multi-story mixed-use towers that elevate city skylines.
                </p>
              </div>
              
              <div className="relative">
                <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#20B2AA]/50 to-transparent" />
                <p className="text-lg text-gray-300 leading-relaxed">
                  We proudly consider ourselves visionaries. Our success lies in the ability to see potential - not just in structures, but in the neighborhoods and people surrounding them. Every site we select and every development we pursue reflects a thoughtful understanding of the area's history, present rhythm, and future promise.
                </p>
              </div>

              <div className="relative">
                <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#20B2AA]/30 to-transparent" />
                <p className="text-lg text-gray-300 leading-relaxed">
                  From revitalizing underutilized lots to creating vibrant, sustainable spaces, we take a holistic approach that goes far beyond the conventional. At GN, we don't follow trends - we set them.
                </p>
              </div>
            </div>

            {/* Right Column - Visual Cards */}
            <div className="space-y-6 slide-in-right">
              {/* Card 1 */}
              <div className="group relative bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 hover:border-[#20B2AA]/50 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-[#20B2AA]/20">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#20B2AA] to-[#189a93] flex items-center justify-center flex-shrink-0 group-hover:rotate-6 transition-transform duration-300">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#20B2AA] transition-colors">Portfolio Diversity</h3>
                    <p className="text-gray-400 text-sm">From boutique apartments to mixed-use towers across the United States</p>
                  </div>
                </div>
              </div>

              {/* Card 2 */}
              <div className="group relative bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 hover:border-[#20B2AA]/50 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-[#20B2AA]/20">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#20B2AA] to-[#189a93] flex items-center justify-center flex-shrink-0 group-hover:rotate-6 transition-transform duration-300">
                    <Users className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#20B2AA] transition-colors">Community-Focused</h3>
                    <p className="text-gray-400 text-sm">Understanding history, present rhythm, and future promise of every neighborhood</p>
                  </div>
                </div>
              </div>

              {/* Card 3 */}
              <div className="group relative bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 hover:border-[#20B2AA]/50 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-[#20B2AA]/20">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#20B2AA] to-[#189a93] flex items-center justify-center flex-shrink-0 group-hover:rotate-6 transition-transform duration-300">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#20B2AA] transition-colors">Trendsetting Vision</h3>
                    <p className="text-gray-400 text-sm">Creating sustainable spaces and setting industry standards, not following them</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center slide-in-up">
            <Link to="/services">
              <Button
                size="lg"
                className="bg-[#20B2AA] hover:bg-[#189a93] text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-200 group"
              >
                Our Services
                <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/about">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white hover:text-black px-8 py-6 text-lg rounded-full transition-all duration-200 group backdrop-blur-sm"
              >
                Our Team
                <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Client Testimonials Section */}
      <section className="relative py-24 bg-gradient-to-b from-teal-100/80 via-teal-50/40 to-white overflow-hidden">
        {/* Subtle background decoration */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#20B2AA] rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-300 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-black">
              Voices of Trust
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real stories from the people who live, invest, and grow with us.
            </p>
          </div>

          {/* Testimonial Carousel */}
          <div className="relative">
            {/* Navigation Arrows */}
            <button
              onClick={() => {
                setCurrentTestimonial((prev) => 
                  prev === 0 ? testimonials.length - 1 : prev - 1
                );
              }}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 z-10 bg-white hover:bg-[#20B2AA] text-gray-800 hover:text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 group"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>

            <button
              onClick={() => {
                setCurrentTestimonial((prev) => 
                  prev === testimonials.length - 1 ? 0 : prev + 1
                );
              }}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 z-10 bg-white hover:bg-[#20B2AA] text-gray-800 hover:text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 group"
              aria-label="Next testimonial"
            >
              <ChevronRight className="h-6 w-6" />
            </button>

            {/* Testimonial Card */}
            <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12 max-w-4xl mx-auto">
              <div className="flex flex-col lg:flex-row items-center gap-8">
                {/* Client Image */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#20B2AA] to-teal-600 rounded-full blur-xl opacity-30" />
                    <img
                      src={testimonials[currentTestimonial].image}
                      alt={testimonials[currentTestimonial].name}
                      className="relative w-32 h-32 lg:w-40 lg:h-40 rounded-full object-cover border-4 border-white shadow-xl"
                    />
                  </div>
                </div>

                {/* Testimonial Content */}
                <div className="flex-1 text-center lg:text-left">
                  {/* Quote Icon */}
                  <div className="text-[#20B2AA] mb-4">
                    <svg className="w-12 h-12 mx-auto lg:mx-0" fill="currentColor" viewBox="0 0 32 32">
                      <path d="M10 8c-3.3 0-6 2.7-6 6v10h10V14H8c0-1.1.9-2 2-2h2V8h-2zm12 0c-3.3 0-6 2.7-6 6v10h10V14h-6c0-1.1.9-2 2-2h2V8h-2z" />
                    </svg>
                  </div>

                  {/* Review Text */}
                  <p className="text-lg lg:text-xl text-gray-700 leading-relaxed mb-6 italic">
                    "{testimonials[currentTestimonial].review}"
                  </p>

                  {/* Client Info */}
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">
                      {testimonials[currentTestimonial].name}
                    </h4>
                    <p className="text-[#20B2AA] font-medium">
                      {testimonials[currentTestimonial].role}
                    </p>
                    {testimonials[currentTestimonial].property && (
                      <p className="text-gray-500 text-sm mt-1">
                        {testimonials[currentTestimonial].property}
                      </p>
                    )}
                  </div>

                  {/* Star Rating */}
                  <div className="flex justify-center lg:justify-start gap-1 mt-4">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className="w-5 h-5 text-yellow-400 fill-current"
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentTestimonial
                      ? 'w-8 bg-[#20B2AA]'
                      : 'w-2 bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with White to Teal Gradient */}
      <section className="relative py-24 overflow-hidden">
        {/* White to Teal gradient background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-white via-teal-50/70 to-teal-100/80" />
        </div>

        {/* Decorative teal elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div 
            className="absolute -top-20 -right-20 w-96 h-96 bg-gradient-to-br from-[#20B2AA]/15 to-transparent rounded-full blur-3xl"
            style={{ transform: `translateY(${scrollY * 0.05}px)` }}
          />
          <div 
            className="absolute -bottom-20 -left-20 w-96 h-96 bg-gradient-to-tr from-teal-300/15 to-transparent rounded-full blur-3xl"
            style={{ transform: `translateY(${scrollY * -0.05}px)` }}
          />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-black">Ready to Build Your Future?</h2>
          <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
            Whether you're an investor, prospective tenant, or partner, we'd love to hear from you.
          </p>
          <Button
            data-testid="get-in-touch-btn"
            size="lg"
            onClick={() => {
              setModalTitle('Get in Touch');
              setModalInterest('');
              setIsModalOpen(true);
            }}
            className="bg-black hover:bg-gray-800 text-white px-10 py-6 text-lg rounded-full hover:-translate-y-1 transition-all duration-200"
          >
            Get in Touch
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalTitle}
        defaultInterest={modalInterest}
        source="general"
        formLocation="homepage_cta"
      />

      {/* Global Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-20px) scale(1.05); }
        }
        
        @keyframes gridMove {
          0% { transform: translateX(0) translateY(0); }
          100% { transform: translateX(100px) translateY(100px); }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-in {
          animation: fadeInUp 0.8s ease-out forwards;
          opacity: 0;
        }

        .scale-in {
          animation: scaleIn 0.6s ease-out forwards;
          opacity: 0;
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.1); }
        }

        /* Smooth scroll behavior */
        html {
          scroll-behavior: smooth;
        }

        /* Add subtle backdrop blur to cards on scroll */
        .card-glass {
          backdrop-filter: blur(10px);
          background: rgba(255, 255, 255, 0.9);
        }
      `}</style>
    </div>
  );
};

export default Home;
